/**
 * IM-005 checkout: re-align warehouse inventory cookies with the basket shipping
 * address when the shopper returns from PDP (or another flow) that overwrote
 * `kl_wh_*` cookies for a different delivery context.
 */

import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';

import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {useShippingMethods} from './use-shipping-methods';
import {useWarehouseInventory} from './use-warehouse-inventory';
import {
  getFirstWhsCodeFromMethods,
  getWhsCodeFromShippingMethod,
} from '../utils/shipping-method-whs-code';
import {
  isCheckoutInventoryContextStale,
  isWarehouseResolutionPreferenceEnabled,
} from '../utils/warehouse-inventory-cookies';

/**
 * @returns {{ isInventorySyncing: boolean }}
 */
export function useCheckoutWarehouseInventorySync() {
  const {site} = useMultiSite();
  const siteId = site?.id || 'kl-au';
  const preferences = site?.preferences || {};
  const location = useLocation();
  const {data: basket} = useCurrentBasket();

  const shippingAddress = basket?.shipments?.[0]?.shippingAddress;
  const selectedMethodId = basket?.shipments?.[0]?.shippingMethod?.id;

  const hasCheckoutShipping = Boolean(
    shippingAddress?.postalCode?.trim() && shippingAddress?.countryCode?.trim()
  );

  const prefEnabled = isWarehouseResolutionPreferenceEnabled(preferences);

  const [syncTick, setSyncTick] = useState(0);

  const bumpSyncCheck = useCallback(() => {
    setSyncTick((t) => t + 1);
  }, []);

  useEffect(() => {
    bumpSyncCheck();
  }, [
    bumpSyncCheck,
    location.pathname,
    basket?.basketId,
    shippingAddress?.postalCode,
    shippingAddress?.stateCode,
    shippingAddress?.countryCode,
  ]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        bumpSyncCheck();
      }
    };

    document.addEventListener('visibilitychange', onVisible);

    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [bumpSyncCheck]);

  const isStale = useMemo(() => {
    if (!hasCheckoutShipping) {
      return false;
    }

    // Re-read cookies when syncTick bumps (visibility / re-enter checkout).
    void syncTick;

    return isCheckoutInventoryContextStale(siteId, shippingAddress);
  }, [hasCheckoutShipping, siteId, shippingAddress, syncTick]);

  const shippingQueryEnabled = Boolean(prefEnabled && hasCheckoutShipping && basket?.basketId);

  const {data: shippingData, isLoading: isShippingMethodsLoading} = useShippingMethods({
    cacheContext: 'checkout',
    enabled: shippingQueryEnabled,
  });

  const whsCode = useMemo(() => {
    const methods = shippingData?.applicableShippingMethods;

    if (!methods?.length) {
      return undefined;
    }

    if (selectedMethodId) {
      const selected = methods.find((m) => m.id === selectedMethodId);
      const fromSelected = getWhsCodeFromShippingMethod(selected);

      if (fromSelected) {
        return fromSelected;
      }
    }

    return getFirstWhsCodeFromMethods(methods);
  }, [shippingData?.applicableShippingMethods, selectedMethodId]);

  const shippingApiActive = Boolean(shippingData?.apiUsed);

  const shouldSync = Boolean(
    prefEnabled && hasCheckoutShipping && isStale && shippingApiActive && whsCode
  );

  const {isLoading: isWarehouseResolving} = useWarehouseInventory({
    whsCode,
    address: shippingAddress,
    shippingApiActive,
    enabled: shouldSync,
  });

  const isAwaitingWhsForSync = Boolean(
    prefEnabled &&
      hasCheckoutShipping &&
      isStale &&
      shippingApiActive &&
      (isShippingMethodsLoading || !whsCode)
  );

  const isInventorySyncing = shouldSync ? isWarehouseResolving : isAwaitingWhsForSync;

  return {isInventorySyncing};
}

export default useCheckoutWarehouseInventorySync;
